<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthClient } from '@/stores/auth-store.ts'
import { useEventStore } from '@/stores/event-store.ts'

const router = useRouter()
const { acceptVerification, hasLocalAccount: isAuthenticated, getDeviceId } = useAuthClient()
const { createOnboardingEvent } = useEventStore()

type SetupStep =
  | 'code'
  | 'authChoice'
  | 'emailCode'
  | 'onboardName'
  | 'onboardBirthday'
  | 'onboardProfile'

type AuthMode = 'signup' | 'login'

const code = ref('')
const name = ref('')
const email = ref('')
const verificationCode = ref('')
const deviceId = ref(getDeviceId())
const step = ref<SetupStep>('code')
const authMode = ref<AuthMode>('signup')
const loading = ref(false)
const errorMsg = ref<string | null>(null)
const infoMsg = ref<string | null>(null)
const pendingVerificationKey = 'timethread-pending-verification-v1'

const restartVerification = () => {
  localStorage.removeItem(pendingVerificationKey)
  verificationCode.value = ''
  step.value = 'code'
  errorMsg.value = null
  infoMsg.value = null
}

const restorePendingVerification = () => {
  if (isAuthenticated.value) {
    localStorage.removeItem(pendingVerificationKey)
    return
  }
  try {
    const raw = localStorage.getItem(pendingVerificationKey)
    if (!raw) return
    const pending = JSON.parse(raw)
    if (
      typeof pending.email !== 'string' || !pending.email.trim() ||
      typeof pending.name !== 'string' ||
      pending.deviceId !== deviceId.value ||
      typeof pending.expiresAt !== 'number' || !Number.isFinite(pending.expiresAt) ||
      pending.expiresAt <= Date.now()
    ) {
      restartVerification()
      infoMsg.value = 'Please start again to request a new email code.'
      return
    }
    email.value = pending.email
    name.value = pending.name
    step.value = 'emailCode'
  } catch {
    restartVerification()
  }
}

const fullName = ref('')
const birthdayDate = ref('')
const birthdayTime = ref('')
const profileImageDataUrl = ref<string | null>(null)
const profileImageInput = ref<HTMLInputElement | null>(null)

const onboardingProgress = computed(() => {
  if (step.value === 'onboardName') return 1
  if (step.value === 'onboardBirthday') return 2
  if (step.value === 'onboardProfile') return 3
  return 0
})

const previousOnboardingStep = computed<SetupStep | null>(() => {
  if (!isAuthenticated.value) return null
  if (step.value === 'onboardBirthday') return 'onboardName'
  if (step.value === 'onboardProfile') return 'onboardBirthday'
  return null
})

const deriveFallbackName = () => {
  const trimmedName = name.value.trim()
  if (trimmedName) return trimmedName
  const emailPrefix = email.value.split('@')[0]?.trim()
  return emailPrefix || 'TimeThread User'
}

const persistOnboardingState = (options?: { completed?: boolean; nextStep?: SetupStep | null }) => {
  const onboardingData = {
    profile: {
      name: fullName.value.trim(),
      birthdayDate: birthdayDate.value || null,
      birthdayTime: birthdayTime.value || null,
      profileImageDataUrl: profileImageDataUrl.value,
    },
    completed: options?.completed ?? false,
    nextStep: options?.nextStep ?? null,
    updatedAt: new Date().toISOString(),
  }

  localStorage.setItem('timethread-onboarding-v1', JSON.stringify(onboardingData))
}

const validateAccessCode = async () => {
  const response = await fetch('/rpc/validate-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.value }),
  })
  if (!response.ok) {
    throw new Error(`Validation failed (${response.status})`)
  }
  const resp = await response.json()
  if (resp?.valid) {
    step.value = 'authChoice'
    infoMsg.value = 'Access code verified. Continue with login or sign up.'
  } else {
    errorMsg.value = 'Invalid access code. Please try again.'
  }
}

const sendVerificationEmail = async () => {
  const response = await fetch('/rpc/users/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code: code.value,
      name: deriveFallbackName(),
      email: email.value,
      deviceId: deviceId.value,
    }),
  })
  const resp = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(resp?.message || `Registration failed (${response.status})`)
  }
  if (resp?.success || resp?.ok) {
    // Keep the email step across mobile browser reloads; never store either code.
    localStorage.setItem(pendingVerificationKey, JSON.stringify({
      email: email.value,
      name: name.value,
      deviceId: deviceId.value,
      expiresAt: Date.now() + 15 * 60_000,
    }))
    step.value = 'emailCode'
    infoMsg.value = 'Check your email. Open the magic link or enter your one-time code.'
  } else {
    errorMsg.value = resp?.message || 'Could not send verification email.'
  }
}

const verifyOneTimeCode = async () => {
  const response = await fetch('/rpc/users/auth/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: email.value,
      deviceId: deviceId.value,
      verificationCode: verificationCode.value,
    }),
  })

  const resp = await response.json()
  if (!response.ok) {
    throw new Error(resp?.error || `Verification failed (${response.status})`)
  }

  if (resp?.accessToken && resp?.refreshToken) {
    acceptVerification(resp)
    localStorage.removeItem(pendingVerificationKey)
    fullName.value = fullName.value || deriveFallbackName()
    step.value = 'onboardName'
    infoMsg.value = null
    return
  }

  errorMsg.value = 'Verification failed. Please request a new code.'
}

const handleSetup = async () => {
  errorMsg.value = null
  infoMsg.value = null

  loading.value = true
  try {
    if (step.value === 'code') {
      await validateAccessCode()
    } else if (step.value === 'authChoice') {
      await sendVerificationEmail()
    } else if (step.value === 'emailCode') {
      await verifyOneTimeCode()
    }
  } catch (e: unknown) {
    errorMsg.value = e instanceof Error ? e.message : 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}

const handleProfileImageChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = () => {
    profileImageDataUrl.value = typeof reader.result === 'string' ? reader.result : null
  }
  reader.readAsDataURL(file)
}

const triggerProfileImagePicker = () => {
  profileImageInput.value?.click()
}

const continueFromName = () => {
  if (!fullName.value.trim()) {
    errorMsg.value = 'Please enter your name to continue.'
    return
  }

  errorMsg.value = null
  persistOnboardingState({ completed: false, nextStep: 'onboardBirthday' })
  step.value = 'onboardBirthday'
}

const continueFromBirthday = () => {
  if (!birthdayDate.value) {
    errorMsg.value = 'Please enter your birthday to continue.'
    return
  }

  errorMsg.value = null
  persistOnboardingState({ completed: false, nextStep: 'onboardProfile' })
  step.value = 'onboardProfile'
}

const finishOnboarding = async () => {
  loading.value = true
  errorMsg.value = null

  await createOnboardingEvent({
    name: fullName.value.trim(),
    birthdayDate: birthdayDate.value,
    birthdayTime: birthdayTime.value || null,
  })

  loading.value = false
  persistOnboardingState({ completed: true, nextStep: null })
  router.replace({ name: 'timeline' })
}

const goBackOneOnboardingStep = () => {
  const previousStep = previousOnboardingStep.value
  if (!previousStep) return

  errorMsg.value = null
  infoMsg.value = null
  persistOnboardingState({
    completed: false,
    nextStep: previousStep === 'onboardBirthday' ? 'onboardBirthday' : null,
  })
  step.value = previousStep
}

const restoreOnboardingState = () => {
  if (!isAuthenticated.value) return
  step.value = 'onboardName'

  try {
    const raw = localStorage.getItem('timethread-onboarding-v1')
    if (!raw) return
    const data = JSON.parse(raw) as {
      profile?: {
        name?: string
        birthday?: string | null
        birthdayDate?: string | null
        birthdayTime?: string | null
        profileImageDataUrl?: string | null
      }
      completed?: boolean
      nextStep?: SetupStep | null
    }

    fullName.value = data.profile?.name ?? fullName.value
    birthdayDate.value = data.profile?.birthdayDate ?? data.profile?.birthday ?? birthdayDate.value
    birthdayTime.value = data.profile?.birthdayTime ?? birthdayTime.value
    profileImageDataUrl.value = data.profile?.profileImageDataUrl ?? profileImageDataUrl.value

    if (data.completed) {
      router.replace({ name: 'timeline' })
      return
    }

    if (!data.completed && (data.nextStep === 'onboardBirthday' || data.nextStep === 'onboardProfile')) {
      step.value = data.nextStep
    }
  } catch {
    // ignore malformed local storage and continue with default flow
  }
}

restorePendingVerification()
restoreOnboardingState()
</script>

<template>
  <div class="text-base-content space-y-5">

    <form
      v-if="step === 'code' || step === 'authChoice' || step === 'emailCode'"
      @submit.prevent="handleSetup"
      class="mx-auto w-[22rem] max-w-full space-y-4"
    >
      <div v-if="step === 'code'" class="space-y-3">
        <h2 class="text-xl font-semibold">Invitation access code</h2>
        <p class="text-sm text-base-content/70">Enter your invitation access code to begin. Your email verification code comes later.</p>
        <label for="access-code" class="block text-sm font-medium mb-1">Access code</label>
        <input id="access-code" v-model="code" type="text" class="w-full input input-bordered" required />
      </div>

      <div v-else-if="step === 'authChoice'" class="space-y-4">
        <div class="tabs tabs-box bg-base-200/60 p-1">
          <button type="button" class="tab flex-1" :class="{ 'tab-active': authMode === 'signup' }" @click="authMode = 'signup'">
            Sign Up
          </button>
          <button type="button" class="tab flex-1" :class="{ 'tab-active': authMode === 'login' }" @click="authMode = 'login'">
            Log In
          </button>
        </div>
        <p class="text-sm text-base-content/70">
          {{
            authMode === 'signup'
              ? 'Create your account and we’ll send a verification email.'
              : 'Sign in with your email and we’ll send you a one-time code.'
          }}
        </p>
        <div>
          <label class="block text-sm font-medium mb-2">Your name{{ authMode === 'login' ? ' (optional)' : '' }}:</label>
          <input v-model="name" type="text" class="w-full input input-bordered" :required="authMode === 'signup'" />
        </div>
        <div>
          <label class="block text-sm font-medium mb-2">Your email:</label>
          <input v-model="email" type="email" class="w-full input input-bordered" required />
        </div>
      </div>

      <div v-else-if="step === 'emailCode'" class="space-y-4">
        <h2 class="text-xl font-semibold">Check your email</h2>
        <p class="text-sm text-base-content/70">Enter the six-digit code sent to {{ email }}. You can return here after checking your email.</p>
        <div>
          <label for="email-code" class="block text-sm font-medium mb-2">One-time code from email:</label>
          <input id="email-code" v-model="verificationCode" type="text" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" maxlength="6" class="w-full input input-bordered" required />
        </div>
        <button type="button" class="btn btn-ghost btn-sm" :disabled="loading" @click="restartVerification">Use a different email or request a new code</button>
      </div>

      <div v-if="errorMsg" class="text-error text-sm">{{ errorMsg }}</div>
      <div v-if="infoMsg" class="text-info text-sm">{{ infoMsg }}</div>

      <button
        type="submit"
        class="btn btn-primary w-full"
        :disabled="loading"
      >
        {{
          loading
            ? 'Please wait…'
            : (step === 'code' ? 'Verify Access Code' : (step === 'authChoice' ? 'Send Verification Email' : 'Verify Email Code'))
        }}
      </button>
    </form>

    <section v-else-if="step === 'onboardName'" class="min-h-[80vh] flex flex-col">
      <div class="space-y-3 pt-6">
        <h2 class="text-3xl leading-tight font-semibold text-[#6975ce]">What&apos;s your name?</h2>
        <p class="text-base leading-relaxed text-base-content/85">Your name is important to help categorize events</p>
      </div>

      <div class="flex-1 flex items-center">
        <div class="mx-auto w-[22rem] max-w-full min-w-0">
          <label class="block text-sm font-medium mb-2 text-base-content/70">Full name</label>
          <input v-model="fullName" type="text" class="input input-bordered w-full bg-base-100 text-base-content" required />
          <div v-if="errorMsg" class="text-error text-sm mt-3">{{ errorMsg }}</div>
        </div>
      </div>

      <div class="mx-auto w-[22rem] max-w-full min-w-0 pb-4 min-h-[11.5rem] flex flex-col justify-end gap-4">
        <button class="btn w-full rounded-full h-14 border-0 bg-[#50c4c3] text-white text-lg font-medium normal-case hover:bg-[#48b8b7]" @click="continueFromName">
          Next
        </button>
        <button class="btn btn-ghost text-base normal-case invisible pointer-events-none" aria-hidden="true" tabindex="-1">
          Back
        </button>
        <div class="mx-auto h-5 w-2/3 rounded-full bg-base-300 overflow-hidden">
          <div class="h-full rounded-full bg-primary" :style="{ width: `${(onboardingProgress / 3) * 100}%` }" />
        </div>
      </div>
    </section>

    <section v-else-if="step === 'onboardBirthday'" class="min-h-[80vh] flex flex-col">
      <div class="space-y-3 pt-6">
        <h2 class="text-3xl leading-tight font-semibold text-[#6975ce]">When is your birthday?</h2>
        <p class="text-base leading-relaxed text-base-content/85">We use this to place moments in your life timeline. Birth time is optional.</p>
      </div>

      <div class="flex-1 flex items-center">
        <div class="mx-auto w-[22rem] max-w-full min-w-0 space-y-4">
          <div>
            <label class="block text-sm font-medium mb-2 text-base-content/70">Birthday date</label>
            <input v-model="birthdayDate" type="date" class="input input-bordered w-full min-w-0 bg-base-100 text-base-content" required />
          </div>

          <div>
            <label class="block text-sm font-medium mb-2 text-base-content/70">Birth time (optional)</label>
            <input v-model="birthdayTime" type="time" class="input input-bordered w-full min-w-0 bg-base-100 text-base-content" />
          </div>

          <div v-if="errorMsg" class="text-error text-sm mt-3">{{ errorMsg }}</div>
        </div>
      </div>

      <div class="mx-auto w-[22rem] max-w-full min-w-0 pb-4 min-h-[11.5rem] flex flex-col justify-end gap-4">
        <button class="btn w-full rounded-full h-14 border-0 bg-[#50c4c3] text-white text-lg font-medium normal-case hover:bg-[#48b8b7]" @click="continueFromBirthday">
          Next
        </button>
        <button class="btn btn-ghost text-base text-base-content/60 normal-case" @click="goBackOneOnboardingStep">Back</button>
        <div class="mx-auto h-5 w-2/3 rounded-full bg-base-300 overflow-hidden">
          <div class="h-full rounded-full bg-primary" :style="{ width: `${(onboardingProgress / 3) * 100}%` }" />
        </div>
      </div>
    </section>

    <section v-else-if="step === 'onboardProfile'" class="min-h-[80vh] flex flex-col">
      <div class="space-y-3 pt-6">
        <h2 class="text-3xl leading-tight font-semibold text-[#6975ce]">Add a profile photo</h2>
        <p class="text-base leading-relaxed text-base-content/85">Your photo helps personalize your profile and makes your timeline easier to scan.</p>
      </div>

      <div class="flex-1 flex items-center justify-center">
        <div class="flex flex-col items-center gap-3">
          <input
            ref="profileImageInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleProfileImageChange"
          />
          <button type="button" class="group relative" @click="triggerProfileImagePicker">
            <div class="avatar">
              <div class="w-32 rounded-full bg-base-100 ring-2 ring-primary/45 transition group-hover:ring-primary">
                <img v-if="profileImageDataUrl" :src="profileImageDataUrl" alt="Profile preview" />
                <div v-else class="h-full w-full flex items-center justify-center text-base-content/45 text-sm">Add Photo</div>
              </div>
            </div>
            <div class="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/35 text-[10px] font-semibold uppercase tracking-wide text-white opacity-0 transition group-hover:opacity-100">
              Change
            </div>
          </button>
        </div>
      </div>

      <div class="mx-auto w-[22rem] max-w-full min-w-0 pb-4 min-h-[11.5rem] flex flex-col justify-end gap-4">
        <button
          class="btn w-full rounded-full h-14 border-0 bg-[#50c4c3] text-white text-lg font-medium normal-case hover:bg-[#48b8b7]"
          :disabled="loading"
          @click="finishOnboarding"
        >
          {{ loading ? 'Please wait…' : 'Next' }}
        </button>
        <button class="btn btn-ghost text-base text-base-content/60 normal-case" @click="goBackOneOnboardingStep">Back</button>
        <div class="mx-auto h-5 w-2/3 rounded-full bg-base-300 overflow-hidden">
          <div class="h-full rounded-full bg-primary" :style="{ width: `${(onboardingProgress / 3) * 100}%` }" />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
</style>
